import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Star, Sparkles, MapPin, Bookmark, BookmarkCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader, SearchBar, FilterChips, LoaderBlock, EmptyState } from '../components/listHelpers';

const CATEGORY_LABELS = {
  restaurants: 'Restaurants',
  salons: 'Salons',
  groceries: 'Groceries',
  events: 'Events & Catering',
  fashion: 'Fashion',
  health: 'Health & Auto',
};

function BusinessCard({ business, savedIds, onToggleSave }) {
  const isSaved = savedIds.has(business.id);
  const img = business.images?.[0];
  return (
    <Link
      to={`/app/businesses/${business.id}`}
      data-testid={`business-card-${business.id}`}
      className="group bg-white border border-[#F4F1DE] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all relative"
    >
      <div className="relative aspect-[4/3] bg-[#FFE5DD]">
        {img ? (
          <img src={img} alt={business.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 className="w-12 h-12 text-[#C8553D]/30" />
          </div>
        )}
        {business.is_sponsored && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#E9C46A] text-white text-[9px] font-bold rounded uppercase">
            Sponsored
          </span>
        )}
        {business.is_featured && !business.is_sponsored && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#C8553D] text-white text-[9px] font-bold rounded uppercase flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" />
            Featured
          </span>
        )}
        <button
          type="button"
          data-testid={`business-save-${business.id}`}
          onClick={(e) => { e.preventDefault(); onToggleSave(business.id); }}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center"
        >
          {isSaved ? (
            <BookmarkCheck className="w-4 h-4 text-[#C8553D]" fill="currentColor" />
          ) : (
            <Bookmark className="w-4 h-4 text-[#6D6875]" />
          )}
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#C8553D]">
            {CATEGORY_LABELS[business.category] || business.category}
          </span>
          {business.avg_rating > 0 && (
            <span className="flex items-center gap-1 text-xs font-bold text-[#2B2D42]">
              <Star className="w-3.5 h-3.5 fill-[#E9C46A] text-[#E9C46A]" />
              {business.avg_rating.toFixed(1)}
              <span className="font-normal text-[#B5B5B5]">({business.review_count || 0})</span>
            </span>
          )}
        </div>
        <h3 className="text-lg font-serif font-bold text-[#2B2D42] line-clamp-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          {business.name}
        </h3>
        <p className="text-xs text-[#6D6875] flex items-center gap-1 mt-0.5">
          <MapPin className="w-3 h-3" /> {business.city}
        </p>
        <p className="text-sm text-[#6D6875] line-clamp-2 mt-2">{business.description}</p>
      </div>
    </Link>
  );
}

export default function Businesses() {
  const [businesses, setBusinesses] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [savedData] = await Promise.all([
          api('/businesses/saved/me').catch(() => ({ businesses: [] })),
        ]);
        setSavedIds(new Set((savedData.businesses || []).map((b) => b.id)));
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category) params.set('category', category);
        if (city) params.set('city', city);
        const data = await api(`/businesses?${params.toString()}`);
        setBusinesses(data.businesses || []);
      } catch (err) {
        toast.error(err.message || 'Failed to load businesses');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, category, city]);

  async function handleToggleSave(id) {
    try {
      const res = await api(`/businesses/${id}/save`, { method: 'POST' });
      setSavedIds((s) => {
        const n = new Set(s);
        if (res.saved) n.add(id);
        else n.delete(id);
        return n;
      });
      toast.success(res.saved ? 'Saved' : 'Removed from saved');
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  const cities = useMemo(() => {
    const s = new Set();
    businesses.forEach((b) => b.city && s.add(b.city));
    return Array.from(s).sort();
  }, [businesses]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="businesses-page">
      <PageHeader title="Businesses" subtitle="African-owned businesses across Canada" testId="businesses-header" />

      <SearchBar value={search} onChange={setSearch} placeholder="Search restaurants, salons, services..." testId="businesses-search" />

      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6875]">Category</p>
        <FilterChips items={Object.values(CATEGORY_LABELS).map((label) => label)} value={CATEGORY_LABELS[category] || ''} onChange={(label) => {
          const key = Object.keys(CATEGORY_LABELS).find((k) => CATEGORY_LABELS[k] === label);
          setCategory(key || '');
        }} testIdPrefix="businesses-cat" />
      </div>

      {cities.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6875]">City</p>
          <FilterChips items={cities} value={city} onChange={setCity} testIdPrefix="businesses-city" />
        </div>
      )}

      {loading ? (
        <LoaderBlock testId="businesses-loading" />
      ) : businesses.length === 0 ? (
        <EmptyState icon={Building2} title="No businesses match your filters" sub="Try adjusting search or filters." testId="businesses-empty" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2" data-testid="businesses-grid">
          {businesses.map((b) => (
            <BusinessCard key={b.id} business={b} savedIds={savedIds} onToggleSave={handleToggleSave} />
          ))}
        </div>
      )}
    </div>
  );
}
